'use client';
import React, { HTMLAttributes, useEffect, useRef } from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { IconClose16 } from '@cineamo/ui/src/icons/ic-close';
import { IconLoading16 } from '@cineamo/ui/src/icons/ic-loading';

function CartItem({
    className,
    title,
    subtitle,
    buttonLabel,
    price,
    children,
    currency,
    isDisabled,
    isProcessing,
    onRemove,
    removeLabel
}: {
    title: string;
    subtitle?: string;
    buttonLabel?: string;
    price?: number;
    children: React.ReactNode;
    currency: string;
    isDisabled: boolean;
    isProcessing?: boolean;
    /** An x beside the price that drops the line, e.g. a seat picked by mistake (no hunting for it on the map). */
    onRemove?: () => void;
    removeLabel?: string;
} & Pick<HTMLAttributes<HTMLDivElement>, 'className'>): React.JSX.Element {
    const formatter = useFormat();
    const detailsRef = useRef<HTMLDetailsElement>(null);
    useEffect(() => {
        if (detailsRef.current) {
            detailsRef.current?.removeAttribute('open');
        }
    }, [isDisabled]);

    return (
        <details
            ref={detailsRef}
            className={cn('w-full group duration-150', className, {
                'cursor-not-allowed': isDisabled,
                'cursor-pointer': !isDisabled,
                'opacity-80': isProcessing
            })}>
            <summary
                aria-disabled={isDisabled}
                // Safari draws its own disclosure triangle on a summary; the card has none.
                className="flex flex-col w-full space-y-[6px] list-none [&::-webkit-details-marker]:hidden"
                onClick={(e) => {
                    if (isDisabled || isProcessing) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                }}>
                <div className="w-full flex flex-col">
                    <div className="flex space-x-[16px]">
                        {/* Type per Figma "Button Seat Selection" (Cinema Websites 5767:26685). */}
                        <span className="grow text-body-bold-16 leading-[26px] tracking-[0.8px] select-none">
                            {title}
                        </span>
                        {isProcessing ? (
                            <IconLoading16 className="animate-spin" />
                        ) : (
                            <span className="text-body-bold-16 leading-[26px] tracking-[0.8px] shrink-0 select-none">
                                {formatter.number.formatCurrency((price ?? 0) / 100, currency)}
                            </span>
                        )}
                        {onRemove && (
                            <button
                                type="button"
                                aria-label={removeLabel}
                                title={removeLabel}
                                disabled={isProcessing}
                                onClick={(e) => {
                                    // Inside the summary: removing must not also fold the card open.
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onRemove();
                                }}
                                className="-my-2 -mr-4 flex size-28 shrink-0 items-center justify-center rounded-4 outline-none opacity-70 hover:opacity-100 focus-visible:ring-2 focus-visible:ring-[var(--primary-color)] disabled:opacity-30">
                                <IconClose16 fill="currentColor" />
                            </button>
                        )}
                    </div>
                    {subtitle && (
                        <span className="text-body-14 font-montserrat-medium leading-[24px] tracking-[0.2px] select-none">
                            {subtitle}
                        </span>
                    )}
                </div>
                {/* Shown even when nothing can change (one category, or not the cart step): dimmed and inert. */}
                {buttonLabel && (
                    <span
                        className={cn(
                            // Text-only actions take the secondary color: the primary often fails contrast as text.
                            'text-[var(--secondary-color,var(--primary-color))] text-overline-12 leading-[20px] tracking-[1.6px]',
                            isDisabled || isProcessing ? 'opacity-50' : 'clickable-element'
                        )}>
                        {buttonLabel}
                    </span>
                )}
            </summary>
            <div
                onClick={() => {
                    detailsRef.current?.removeAttribute('open');
                }}>
                {children}
            </div>
        </details>
    );
}

export default CartItem;
